import { Injectable } from "@angular/core";
import { DbDaoBase } from "../../../../../gyzmo-commons/dao/db/base/db.dao.base";
import { NotImplementedError } from "../../../../../gyzmo-commons/helpers/notImplementedError";
import { AppSqlProvider } from "../../../../../gyzmo-commons/persistence/app.sql.provider";
import { LoggerService } from "../../../../../gyzmo-commons/services/logs/logger.service";
import { Checklist } from "../../../../models/inspection/v2/checklist.model";
import { ChecklistElementDbDaoV2 } from "./checklistElement.db.dao";

@Injectable({
    providedIn: "root",
})
export class ChecklistDbDaoV2 extends DbDaoBase<Checklist> {
    constructor(
        logger: LoggerService,
        private sqlProvider: AppSqlProvider,
        private checklistElementDbDao: ChecklistElementDbDaoV2) {
        super(logger);
    }

    getList(ids: string[], inspectionId: string, hydrate: boolean = false): Promise<Checklist[]> {
        if (ids.length === 0) {
            return Promise.resolve([]);
        }

        const placeholders = ids.map(() => "?").join(",");
        const selectQuery = `SELECT * FROM ${ Checklist.TABLENAME } WHERE id IN (${ placeholders }) ORDER BY _order;`;

        return this.sqlProvider.query(selectQuery, ids)
            .then(data => {
                if (data.rows.length <= 0) {
                    return [];
                }
                let checklists: Checklist[] = [];
                for (const item of data.rows) {
                    let checklist = this.rowToModel(item);
                    checklists.push(checklist);
                }
                let hydratationPromises = [];
                if (hydrate) {
                    checklists.forEach(checklist => {
                        hydratationPromises.push(this.checklistElementDbDao.getByInspectionIdAndChecklistId(inspectionId, checklist.id, hydrate)
                            .then(value => {
                                checklist.elements = value;
                            }));
                    });
                }
                return Promise.all(hydratationPromises)
                    .then(() => {
                        return checklists;
                    });
            })
            .catch(reason => {
                this.logSqlError(reason);
                return null;
            });
    }

    public async createIndexes(): Promise<void> {
        // Implémentation des indexes si nécessaire
    }

    public createTable(): Promise<void> {
        const query = `CREATE TABLE IF NOT EXISTS ${ Checklist.TABLENAME } (
            id TEXT PRIMARY KEY,
            wording TEXT,
            _order TEXT
        );`;
        return this.sqlProvider.query(query, [])
            .then(async () => {
                await this.createIndexes();
            })
            .catch(reason => {
                this.logSqlError(reason);
                return null;
            });
    }

    delete(id: string): Promise<any> {
        const deleteQuery = `DELETE FROM ${ Checklist.TABLENAME } WHERE id = ?;`;
        return this.sqlProvider.query(deleteQuery, [id]);
    }

    deleteAll(): Promise<any> {
        const deleteAllQuery = `DELETE FROM ${ Checklist.TABLENAME };`;
        return this.sqlProvider.query(deleteAllQuery, []);
    }

    public get(id: string, hydrate: boolean = false): Promise<Checklist> {
        throw new NotImplementedError();
    }

    public getTableName(): string {
        return Checklist.TABLENAME;
    }

    protected rowToModel(row: any): Checklist {
        let checklist = new Checklist();
        checklist.id = row.id;
        checklist.wording = row.wording;
        checklist.order = row._order;
        return checklist;
    }

    public save(checklist: Checklist): Promise<Checklist> {
        let promises = [];
        checklist.elements.forEach(element => {
            promises.push(this.checklistElementDbDao.save(element));
        });
        return Promise.all(promises)
            .then(() => {
                const insertQuery = `INSERT OR REPLACE INTO ${ Checklist.TABLENAME } (id, wording, _order) VALUES (?, ?, ?);`;
                const params = [
                    checklist.id,
                    checklist.wording,
                    checklist.order,
                ];
                return this.sqlProvider.query(insertQuery, params)
                    .then(() => {
                        return checklist;
                    })
                    .catch(reason => {
                        this.logSqlError(reason);
                        return null;
                    });
            });
    }
}
